import { HeroIcon } from '@components/HeroIcon'
import { View, Text, YStack } from 'tamagui'
import { useActivityStore } from '@stores/useActivityStore'
import { ButtonRetry } from './QuestionFooter'
import { LazyQueryExecFunction, OperationVariables } from '@apollo/client'
import { IActivityData } from '@dtos/Activity'
import { useMemo } from 'react'

interface IActivityCardMessageProps {
  progressCardId: string
  enrollmentId: string
  getActivity: LazyQueryExecFunction<IActivityData, OperationVariables>
  mode: 'edit' | 'readonly' | 'retry'
  updateEnrollmentProgress: () => void
}

export function ActivityCardMessage({
  progressCardId,
  enrollmentId,
  getActivity,
  mode,
  updateEnrollmentProgress,
}: IActivityCardMessageProps) {
  const { activity } = useActivityStore()
  const isApproved = useMemo(() => {
    return activity?.examAnswer?.approved
  }, [activity])
  const score = activity?.examAnswer?.score || 0
  const totalScore = activity?.maxGrade || 0
  const percentage = Math.round((score / totalScore) * 100)
  const minGrade = activity?.minGrade || 0

  const text = useMemo(() => {
    if (minGrade === 0) {
      return `Esta atividade não possui uma pontuação mínima.`
    }

    if (isApproved) {
      return `Parabéns pela aprovação! Você acertou ${percentage}% das questões.`
    }

    return `Você não alcançou a pontuação mínima.`
  }, [isApproved, percentage, minGrade])

  const remainingAttempts =
    (activity?.maxAttempts || 0) - (activity?.retryCount || 0)

  const textRetry = `Não se preocupe, ainda restam ${remainingAttempts} tentativas.`

  return (
    <View>
      <View
        borderWidth={1}
        borderColor={isApproved ? '$success-300' : '$danger-300'}
        h={12}
        borderTopLeftRadius="$border-radius-lg"
        borderTopRightRadius="$border-radius-lg"
        backgroundColor={isApproved ? '$success-300' : '$danger-300'}
      ></View>

      <YStack
        alignItems="flex-start"
        borderWidth={1}
        borderColor="$neutral-200"
        borderBottomRightRadius="$border-radius-lg"
        borderBottomLeftRadius="$border-radius-lg"
        p={'$spacing-inset-xs'}
        gap={'$spacing-inset-xs'}
      >
        <View
          backgroundColor={'$neutral-100'}
          w={32}
          h={32}
          borderRadius="$border-radius-pill"
          alignItems="center"
          justifyContent="center"
        >
          <HeroIcon
            icon={['fal', isApproved ? 'trophy-star' : 'file-signature']}
            size={16}
            color={isApproved ? '$success-300' : '$danger-300'}
          />
        </View>

        <Text
          ff="$mulishSemiBold"
          fontSize="$font-size-xs"
          color="$neutral-800"
          fontWeight={700}
        >
          {text}
        </Text>

        {activity?.retryPermitted && (
          <Text
            ff="$mulishSemiBold"
            fontSize="$font-size-xs"
            color="$neutral-800"
            fontWeight={400}
          >
            {textRetry}
          </Text>
        )}

        {activity?.retryPermitted && (
          <ButtonRetry
            progressCardId={progressCardId}
            enrollmentId={enrollmentId}
            getActivity={getActivity}
            mode={mode}
            updateEnrollmentProgress={updateEnrollmentProgress}
          />
        )}
      </YStack>
    </View>
  )
}
